import React, { useRef } from 'react';
import { Form } from '@unform/web';
import { FormHandles } from '@unform/core';
import Input from '../Forms/Input';
import MaskedInput from '../Forms/MaskedInput';
import SelectApi from '../Forms/SelectApi';
import SelectApiDirect from '../Forms/SelectApiDirect';
import SelectSimple from '../Forms/SelectSimple';
import JsonInputContainer from '../Forms/JsonInput';
import Datepicker from '../Forms/DatePicker';
import DatepickerHour from '../Forms/DatePickerHour';
import Hourpicker from '../Forms/HourPicker';
import { Field } from './styles';
import Button from '../Forms/Button';
import CurrencyInput from '../Forms/MaskedCurrencyInput';
import RichText from '../Forms/RichText';
import UploadImage from '../Forms/UploadImage';
import ImageGalleryInput from '../Forms/ImageGalleryInput';
import JsonList from '../Forms/JsonList';
import JsonListIncluder from '../Forms/JsonListIncluder';
import JsonListMultiple from '../Forms/JsonListMultiple';
import ArrayInputIncluder from '../Forms/ArrayInputIncluder';
import JsonInputList from '../Forms/JsonInputList';
import JsonListMultipleProducts from '../Forms/JsonListMultipleProducts';
import ImageSingleGalleryInput from '../Forms/ImageSingleGalleryInput';

import TextArea from '../Forms/TextareaInput';

interface IInputModel {
  model: string;
  name: string;
  type?: string;
  callback?: Function;
  mask?: string;
  label: string;
}

interface IListInput {
  label: string;
  name: string;
}

interface IInputJsonModel {
  model: string;
  name: string;
  type: string;
  list: Array<IListInput>;
  defaultValue?: Array<Record<string, any>>;
  callback?: Function;
  mask?: string;
  label: string;
  show?: Array<string> | string;
}

interface ISelectOptions {
  text: string;
  value: string;
}

interface IJsonInputModel {
  model: string;
  name: string;
  storageType: 'id' | 'json';
  callback?: Function;
  label: string;
  endpoint: string;
  searchRender: Function;
  bodyRender: Function;
}

interface ISelectApiModel {
  model: string;
  name: string;
  type: string;
  callback?: Function;
  mask?: string;
  label: string;
  endpoint: string;
  loadOptions: Function;
  alias: string;
}

interface IImage {
  url?: string;
  title?: string;
  description?: string;
  link_url?: string;
}


interface IInputImageModel {
  model: string;
  name: string;
  type: string;
  list: Array<IImage>;
  callback?: Function;
  mask?: string;
  label: string;
}

interface ISelectSimpleModel {
  model: string;
  name: string;
  type: string;
  callback?: Function;
  mask?: string;
  label: string;
  options: Array<ISelectOptions>;
}

interface IInput {
  name: string;
  type: string;
  callback?: Function;
}

interface IMaskedInput {
  name: string;
  type: string;
  callback?: Function;
  mask: string;
}

interface IFormComponent {
  schema: Record<string, IInputModel>;
  dataInfo?: Record<string, IInputModel>;
  setCallback?: Function;
}

export const sanitizeData = {
  currencyInput: value =>
    value ? parseInt(value.toString().replace(/[^0-9]/g, ''), 10) : 0,
};

export const sanitizeDataObject = (
  data: Record<string, any>,
  formSchema: Record<string, any>,
) => {
  const newData = Object.keys(data).reduce(
    (prev: Record<string, any>, key: string) => {
      prev[key] =
        formSchema[key] &&
          formSchema[key].model &&
          sanitizeData[formSchema[key].model]
          ? sanitizeData[formSchema[key].model](data[key])
          : data[key];
      return prev;
    },
    {},
  );
  return newData;
};

const FormComponent: React.FC<IFormComponent> = ({
  schema,
  dataInfo,
  setCallback,
}): JSX.Element => {
  const formRef = useRef<FormHandles>(null);
  const handleSubmit = async (data: Record<string, any>): Promise<void> => {
    if (setCallback) {
      await setCallback(data, formRef);
    }
  };

  const ValidInputs = (input: IInputModel): JSX.Element => {
    const inputs: Record<string, Function> = {
      input: (inputInfo: IInputModel) => (
        <Field>

          <Input
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            type={inputInfo.type || 'text'}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      textareaInput: (inputInfo: IInputModel) => (
        <Field>

          <TextArea

            placeholder={inputInfo.label}
            name={inputInfo.name}
            type={inputInfo.type || 'text'}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      singleImage: (inputInfo: IInputImageModel) => (
        <Field>

          <ImageSingleGalleryInput
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
            list={inputInfo?.list || [{ name: 'url', label: 'Imagem' }]}
          />
        </Field>
      ),
      jsonInputList: (inputInfo: IInputJsonModel) => (
        <Field>

          <JsonInputList
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            defaultItem={inputInfo.defaultValue}
            name={inputInfo.name}
            show={inputInfo.show}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      jsonList: (inputInfo: IInputJsonModel) => (
        <Field>

          <JsonList
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      jsonListMultipleProducts: (inputInfo: IInputJsonModel) => (
        <Field>

          <JsonListMultipleProducts
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            defaultItem={inputInfo.defaultValue}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      ArrayInputIncluder: (inputInfo: IInputJsonModel) => (
        <Field>

          <ArrayInputIncluder
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            name={inputInfo.name}
            type={inputInfo.type}
            label={inputInfo?.label}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      jsonListMultiple: (inputInfo: IInputJsonModel) => (
        <Field>

          <JsonListMultiple
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            defaultItem={inputInfo.defaultValue}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      jsonListIncluder: (inputInfo: IInputJsonModel) => (
        <Field>

          <JsonListIncluder
            formRef={formRef}
            placeholder={inputInfo.label}
            list={inputInfo.list}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      imageGalleryInput: (inputInfo: IInputImageModel) => (
        <Field>

          <ImageGalleryInput
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
            list={inputInfo.list}
          />
        </Field>
      ),
      uploadImage: (inputInfo: IInputModel) => (
        <Field>

          <UploadImage
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      richText: (inputInfo: IInputModel) => (
        <Field>

          <RichText
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            type={inputInfo.type || 'text'}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      maskedInput: (inputInfo: IInputModel) => (
        <Field>

          <MaskedInput
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            type={inputInfo.type}
            mask={inputInfo.mask || ''}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      currencyInput: (inputInfo: IInputModel) => (
        <Field>

          <CurrencyInput
            formRef={formRef}
            placeholder={inputInfo.label}
            name={inputInfo.name}
            type={inputInfo.type}
            mask={inputInfo.mask || ''}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),

      selectSimple: (inputInfo: ISelectSimpleModel) => (
        <Field>

          <SelectSimple
            formRef={formRef}
            label={inputInfo.label}
            name={inputInfo.name}
            options={inputInfo.options}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      selectApiDirect: (inputInfo: ISelectApiModel) => (
        <Field>

          <SelectApiDirect
            formRef={formRef}
            alias={inputInfo.alias}
            label={inputInfo.label}
            name={inputInfo.name}
            endpoint={inputInfo.endpoint}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),

      selectApi: (inputInfo: ISelectApiModel) => (
        <Field>

          <SelectApi
            formRef={formRef}
            alias={inputInfo.alias}
            label={inputInfo.label}
            name={inputInfo.name}
            endpoint={inputInfo.endpoint}
            setCallback={inputInfo.callback || undefined}
          />
        </Field>
      ),
      JsonInput: (inputInfo: IJsonInputModel) => {
        const {
          searchRender,
          bodyRender,
          storageType = 'id',
          endpoint,
          name,
          label,
        } = inputInfo;
        return (
          <Field>
            <label>{label}</label>
            <JsonInputContainer
              storageType={storageType}
              name={name}
              endpoint={endpoint}
              searchRender={searchRender}
              bodyRender={bodyRender}
            />
          </Field>
        );
      },
      datePicker: (inputInfo: IInputModel) => (
        <Field>

          <Datepicker placeholder={inputInfo.label} name={inputInfo.name} />
        </Field>
      ),
      datePickerHour: (inputInfo: IInputModel) => (
        <Field>

          <DatepickerHour placeholder={inputInfo.label} name={inputInfo.name} />
        </Field>
      ),
      hourPicker: (inputInfo: IInputModel) => (
        <Field>

          <Hourpicker placeholder={inputInfo.label} name={inputInfo.name} />
        </Field>
      ),
    };
    return inputs[input.model](input);
  };

  const getDataInfo = (
    schemaList: Record<string, any>,
  ): Record<string, any> => {
    const data = Object.keys(schemaList).reduce(
      (prev: Record<string, any>, elem: string) => {
        if (schemaList[elem]) {
          prev[schemaList[elem].name] = schemaList[elem].defaultValue || '';
        }
        return prev;
      },
      {},
    );

    return data;
  };

  const getForm = (schemaList: Record<string, IInputModel>) => {
    const data = dataInfo || getDataInfo(schemaList);

    return (
      <>
        {Object.keys(schemaList).map(key => (
          <Form
            style={{
              minWidth: '100%',
              display: 'flex',
              flexDirection: 'column-reverse',
              justifyContent: 'space-between',
              alignItems: 'center',
              borderTop: '2px solid #444',
              padding: '10px 0',
            }}
            ref={formRef}
            initialData={data}
            onSubmit={handleSubmit}
          >
            {ValidInputs(schemaList[key])}

            <Button style={{ maxWidth: '70px', margin: 0 }} type="submit">
              Salvar
            </Button>
          </Form>
        ))}
      </>
    );
  };

  return getForm(schema);
};

export default FormComponent;
